import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { dataTestIds } from '@velasca/constants'
import { ChevronRightIcon } from '@velasca/icons/chevronRight'
import type { ProductSize } from '@velasca/types'
import { SansText } from '@velasca/ui/sans-text'
import {
  StyledButton,
  StyledSizeList,
  StyledSizeOption,
  StyledSizeSelector,
} from './SizeSelector.styles'

interface SizeSelectorProps {
  sizes: ProductSize[]
  onSizeChange: (value: string) => void
  selectedSize: string | null
  isGiftCard?: boolean
}

const SizeSelector = ({
  sizes,
  selectedSize,
  onSizeChange,
  isGiftCard = false,
}: SizeSelectorProps) => {
  const { t } = useTranslation('product')
  const [isOpen, setIsOpen] = useState(false)

  const backInStockAlert = (t('notifyMeTitle') || '').toUpperCase()
  const wrapperRef = useRef<HTMLDivElement>(null)

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen)
  }

  const handleSelectSize = (size: ProductSize) => {
    onSizeChange(size.id)
    setIsOpen(false)
  }

  // eslint-disable-next-line fsecond/valid-event-listener
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        event.target instanceof Node &&
        !wrapperRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [wrapperRef])

  const selectedSizeObject = sizes.find((size) => size.id === selectedSize)

  return (
    <StyledSizeSelector ref={wrapperRef}>
      <StyledButton
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="size-list"
        type="button"
        className={isOpen ? 'open' : ''}
        onClick={handleToggleDropdown}
      >
        <SansText
          uppercase
          as="div"
          className="size-selected-label"
          fontWeight="500"
          color={
            !selectedSizeObject || selectedSizeObject?.isAvailable
              ? 'dark'
              : 'gray'
          }
        >
          {!selectedSizeObject &&
            (isGiftCard ? t('chooseSizeGiftCard') : t('chooseSize'))}
          {selectedSizeObject && (
            <>
              <span>{selectedSizeObject.name}</span>
              {!selectedSizeObject.isAvailable && (
                <span>{backInStockAlert}</span>
              )}
            </>
          )}
        </SansText>

        <ChevronRightIcon aria-hidden="true" />
      </StyledButton>

      <StyledSizeList id="size-list" role="listbox" opened={isOpen}>
        {sizes.map((size) => (
          <StyledSizeOption
            isOutOfStock={!size.isAvailable}
            key={size.id}
            role="option"
            aria-selected={selectedSize === size.id}
            tabIndex={0}
            data-testid={
              size?.isAvailable ? dataTestIds.SIZE_SELECTOR_ITEM : undefined
            }
            onClick={() => {
              handleSelectSize(size)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSelectSize(size)
              }
            }}
          >
            <SansText
              as="span"
              fontWeight="500"
              color={!size.isAvailable ? 'gray' : 'dark'}
            >
              {size.name}
            </SansText>
            {!size.isAvailable && (
              <SansText as="span" fontWeight="500" color="gray">
                {backInStockAlert}
              </SansText>
            )}
          </StyledSizeOption>
        ))}
      </StyledSizeList>
    </StyledSizeSelector>
  )
}

export default SizeSelector
